import { getApiBaseUrl, requestJson } from "@/lib/api";
import type {
  ClientType,
  FulfillmentMode,
  OrderDetail,
  OrderEventType,
  OrderProductionListItem,
  ProductionItemStatus,
  OrderStatus,
  OrderSummary,
} from "@/lib/types";

interface OrderHistoryEventDto {
  type: OrderEventType;
  status?: OrderStatus | null;
  title: string;
  details?: string | null;
  actorEmail?: string | null;
  actorRole?: "CLIENT" | "ADMIN" | null;
  at: string;
}

interface OrderItemDto {
  id: string;
  externalId: string;
  productId: string;
  productName: string;
  emoji: string;
  imageUrl?: string | null;
  category: string;
  cut: string;
  unit: string;
  quantity: number;
  pricePerUnit?: number | null;
  wastePercent?: number | null;
  isProcessed: boolean;
  cancelledAt?: string | null;
  observation?: string | null;
}

interface OrderSummaryDto {
  id: string;
  number?: number | null;
  clientId: string;
  clientName: string;
  clientType: ClientType;
  createdAt: string;
  deliveryWindow?: string | null;
  deliveryDate?: string | null;
  status: OrderStatus;
  purchaseStartedAt?: string | null;
  productionScheduledAt?: string | null;
  scheduledProductionDate?: string | null;
  productionAutoAdvancedPending?: boolean | null;
  expeditionScheduledAt?: string | null;
  expeditionReleasedAt?: string | null;
  routeStartedAt?: string | null;
  deliveredAt?: string | null;
  cancelledAt?: string | null;
  purchased: boolean;
  itemCount: number;
  itemPreview: string[];
  total?: number | null;
  lastUpdatedAt: string;
  priceTableId?: string | null;
  fulfillmentMode?: FulfillmentMode | null;
}

export interface OrderDetailDto {
  id: string;
  number?: number | null;
  clientId: string;
  clientName: string;
  clientType: ClientType;
  deliveryUnit?: OrderDeliveryUnitDto | null;
  createdAt: string;
  deliveryWindow?: string | null;
  deliveryDate?: string | null;
  status: OrderStatus;
  purchaseStartedAt?: string | null;
  productionScheduledAt?: string | null;
  scheduledProductionDate?: string | null;
  productionAutoAdvancedPending?: boolean | null;
  expeditionScheduledAt?: string | null;
  expeditionReleasedAt?: string | null;
  routeStartedAt?: string | null;
  deliveredAt?: string | null;
  cancelledAt?: string | null;
  purchased: boolean;
  items: OrderItemDto[];
  purchaseList?: OrderPurchaseListItemDto[] | null;
  productionList?: OrderProductionListItemDto[] | null;
  expeditionList?: OrderExpeditionListItemDto[] | null;
  total?: number | null;
  notes?: string | null;
  history: OrderHistoryEventDto[];
  historyTotal?: number | null;
  procurementOrderId?: string | null;
  productionOrderId?: string | null;
  priceTableId?: string | null;
  deliveryConfirmation?: DeliveryConfirmationDto | null;
  assignedDriverId?: string | null;
  assignedDriverName?: string | null;
  fulfillmentMode?: FulfillmentMode | null;
}

interface OrderDeliveryUnitDto {
  id: string;
  externalCode: string;
  name: string;
  address?: string | null;
  district?: string | null;
  city?: string | null;
  postalCode?: string | null;
  deliveryWindowText?: string | null;
  email?: string | null;
}

interface DeliveryConfirmationDto {
  id: string;
  orderId: string;
  nfNumber: string;
  driverName: string;
  driverEmail?: string | null;
  deliveredAt: string;
  truckTemperature: number;
  receiverName: string;
  receiverDocument: string;
  signatureUrl: string;
  receiptPhotoUrl: string;
}

interface OrderProductionListItemDto {
  productId: string;
  productName: string;
  emoji?: string | null;
  imageUrl?: string | null;
  category: string;
  unit: string;
  cut?: string | null;
  requestedQuantity: number;
  purchasedQuantity: number;
  productionQuantity: number;
  isProcessed: boolean;
  status: ProductionItemStatus;
  useStock?: boolean | null;
  producedQuantity?: number | null;
  reason?: string | null;
  productionUpdatedAt?: string | null;
  productionUpdatedBy?: string | null;
  observation?: string | null;
}

interface OrderExpeditionListItemDto {
  productId: string;
  productName: string;
  emoji?: string | null;
  imageUrl?: string | null;
  category?: string | null;
  unit: string;
  cut?: string | null;
  requestedQuantity: number;
  loadedQuantity?: number | null;
  isInNatura: boolean;
  status: "PENDENTE" | "CARREGADO" | "EM_FALTA";
  reason?: string | null;
  expeditionUpdatedAt?: string | null;
  expeditionUpdatedBy?: string | null;
  productionStatus?: "PENDENTE" | "EM_ANDAMENTO" | "PRONTO" | "PARCIAL" | "SEM_MERCADORIA" | "CANCELADO" | null;
  producedQuantity?: number | null;
}

interface OrderCheckoutPayload {
  deliveryWindow?: string;
  notes?: string;
  deliveryUnitId?: string;
  /** Reconhece mudança de preço desde a montagem do carrinho (ex.: virada de vigência de negociação). */
  priceChangeAck?: boolean;
}

export type OrderChannel = "WEB" | "WHATSAPP" | "TELEPHONE" | "IN_PERSON" | "OTHER";

export interface ManualOrderItemPayload {
  productId: string;
  unit: string;
  quantity: number;
  cut?: string | null;
  observation?: string | null;
}

export interface ManualOrderPayload {
  customerId: string;
  channel: OrderChannel;
  channelDetail?: string | null;
  deliveryWindow?: string | null;
  deliveryUnitId?: string | null;
  notes?: string | null;
  items: ManualOrderItemPayload[];
}

interface OrderPurchaseListItemDto {
  productId: string;
  productName: string;
  emoji?: string | null;
  imageUrl?: string | null;
  category: string;
  unit: string;
  requestedQuantity: number;
  purchaseQuantity: number;
  wastePercent?: number | null;
  sourceItemIds: string[];
  purchased: boolean;
  useStock?: boolean | null;
  purchasedQuantity?: number | null;
  reason?: string | null;
  purchaseUpdatedAt?: string | null;
  purchaseUpdatedBy?: string | null;
}

interface OrderUpdatePayload {
  deliveryWindow?: string | null;
  notes?: string | null;
  items?: OrderItemUpdatePayload[] | null;
}

interface OrderItemUpdatePayload {
  externalId?: string;
  productId: string;
  unit: string;
  quantity: number;
  cut?: string | null;
  observation?: string | null;
}

interface OrderStatusPayload {
  status: OrderStatus;
}

export function formatOrderNumber(number: number | null | undefined, fallback?: string): string {
  if (number == null) return fallback ?? "—";
  return `NX-${String(number).padStart(6, "0")}`;
}

function toOrderSummary(dto: OrderSummaryDto): OrderSummary {
  return {
    id: dto.id,
    number: dto.number ?? undefined,
    clientId: dto.clientId,
    clientName: dto.clientName,
    clientType: dto.clientType,
    createdAt: dto.createdAt,
    deliveryWindow: dto.deliveryWindow ?? undefined,
    deliveryDate: dto.deliveryDate ?? undefined,
    status: dto.status,
    purchaseStartedAt: dto.purchaseStartedAt ?? undefined,
    productionScheduledAt: dto.productionScheduledAt ?? undefined,
    scheduledProductionDate: dto.scheduledProductionDate ?? undefined,
    productionAutoAdvancedPending: dto.productionAutoAdvancedPending ?? false,
    expeditionScheduledAt: dto.expeditionScheduledAt ?? undefined,
    expeditionReleasedAt: dto.expeditionReleasedAt ?? undefined,
    routeStartedAt: dto.routeStartedAt ?? undefined,
    deliveredAt: dto.deliveredAt ?? undefined,
    cancelledAt: dto.cancelledAt ?? undefined,
    purchased: dto.purchased,
    itemCount: dto.itemCount,
    itemPreview: dto.itemPreview,
    total: dto.total ?? undefined,
    lastUpdatedAt: dto.lastUpdatedAt,
    priceTableId: dto.priceTableId ?? undefined,
    fulfillmentMode: dto.fulfillmentMode ?? undefined,
  };
}

export function toOrderDetail(dto: OrderDetailDto): OrderDetail {
  const items = dto.items.map((item) => ({
    id: item.id,
    externalId: item.externalId,
    productId: item.productId,
    productName: item.productName,
    emoji: item.emoji,
    imageUrl: item.imageUrl ?? undefined,
    category: item.category,
    cut: item.cut,
    unit: item.unit,
    quantity: item.quantity,
    pricePerUnit: item.pricePerUnit ?? undefined,
    wastePercent: item.wastePercent ?? undefined,
    isProcessed: item.isProcessed,
    cancelledAt: item.cancelledAt ?? undefined,
    observation: item.observation ?? undefined,
  }));
  const history = dto.history.map((event) => ({
    type: event.type,
    status: event.status ?? undefined,
    title: event.title,
    details: event.details ?? undefined,
    actorEmail: event.actorEmail ?? undefined,
    actorRole: event.actorRole ?? undefined,
    at: event.at,
  }));
  const productionList: OrderProductionListItem[] = (dto.productionList ?? []).map((item) => ({
    productId: item.productId,
    productName: item.productName,
    emoji: item.emoji ?? undefined,
    imageUrl: item.imageUrl ?? undefined,
    category: item.category,
    unit: item.unit,
    cut: item.cut ?? null,
    requestedQuantity: item.requestedQuantity,
    purchasedQuantity: item.purchasedQuantity,
    productionQuantity: item.productionQuantity,
    isProcessed: item.isProcessed,
    status: item.status,
    useStock: item.useStock ?? false,
    producedQuantity: item.producedQuantity ?? undefined,
    reason: item.reason ?? undefined,
    productionUpdatedAt: item.productionUpdatedAt ?? undefined,
    productionUpdatedBy: item.productionUpdatedBy ?? undefined,
    observation: item.observation ?? null,
  }));
  const expeditionList = (dto.expeditionList ?? []).map((item) => ({
    productId: item.productId,
    productName: item.productName,
    emoji: item.emoji ?? null,
    imageUrl: item.imageUrl ?? null,
    category: item.category ?? null,
    unit: item.unit,
    cut: item.cut ?? null,
    requestedQuantity: item.requestedQuantity,
    loadedQuantity: item.loadedQuantity ?? null,
    isInNatura: item.isInNatura,
    status: item.status,
    reason: item.reason ?? null,
    expeditionUpdatedAt: item.expeditionUpdatedAt ?? null,
    expeditionUpdatedBy: item.expeditionUpdatedBy ?? null,
    productionStatus: item.productionStatus ?? null,
    producedQuantity: item.producedQuantity ?? null,
  }));
  const purchaseList = (dto.purchaseList ?? []).map((item) => ({
    productId: item.productId,
    productName: item.productName,
    emoji: item.emoji ?? undefined,
    imageUrl: item.imageUrl ?? undefined,
    category: item.category,
    unit: item.unit,
    requestedQuantity: item.requestedQuantity,
    purchaseQuantity: item.purchaseQuantity,
    wastePercent: item.wastePercent ?? undefined,
    sourceItemIds: item.sourceItemIds,
    purchased: item.purchased,
    useStock: item.useStock ?? false,
    purchasedQuantity: item.purchasedQuantity ?? undefined,
    reason: item.reason ?? undefined,
    purchaseUpdatedAt: item.purchaseUpdatedAt ?? undefined,
    purchaseUpdatedBy: item.purchaseUpdatedBy ?? undefined,
  }));
  const lastUpdatedAt = history[history.length - 1]?.at ?? dto.createdAt;

  return {
    id: dto.id,
    number: dto.number ?? undefined,
    clientId: dto.clientId,
    clientName: dto.clientName,
    clientType: dto.clientType,
    createdAt: dto.createdAt,
    deliveryWindow: dto.deliveryWindow ?? undefined,
    deliveryDate: dto.deliveryDate ?? undefined,
    status: dto.status,
    purchaseStartedAt: dto.purchaseStartedAt ?? undefined,
    productionScheduledAt: dto.productionScheduledAt ?? undefined,
    scheduledProductionDate: dto.scheduledProductionDate ?? undefined,
    productionAutoAdvancedPending: dto.productionAutoAdvancedPending ?? false,
    expeditionScheduledAt: dto.expeditionScheduledAt ?? undefined,
    expeditionReleasedAt: dto.expeditionReleasedAt ?? undefined,
    routeStartedAt: dto.routeStartedAt ?? undefined,
    deliveredAt: dto.deliveredAt ?? undefined,
    cancelledAt: dto.cancelledAt ?? undefined,
    purchased: dto.purchased,
    itemCount: items.length,
    // Todos os nomes distintos — a busca das listas admin filtra por este campo (truncado só no display).
    itemPreview: Array.from(new Set(items.map((item) => item.productName))),
    total: dto.total ?? undefined,
    lastUpdatedAt,
    priceTableId: dto.priceTableId ?? undefined,
    deliveryUnit: dto.deliveryUnit
      ? {
          id: dto.deliveryUnit.id,
          externalCode: dto.deliveryUnit.externalCode,
          name: dto.deliveryUnit.name,
          address: dto.deliveryUnit.address ?? null,
          district: dto.deliveryUnit.district ?? null,
          city: dto.deliveryUnit.city ?? null,
          postalCode: dto.deliveryUnit.postalCode ?? null,
          deliveryWindowText: dto.deliveryUnit.deliveryWindowText ?? null,
          email: dto.deliveryUnit.email ?? null,
        }
      : null,
    items,
    purchaseList,
    productionList,
    expeditionList,
    notes: dto.notes ?? undefined,
    history,
    historyTotal: dto.historyTotal ?? history.length,
    procurementOrderId: dto.procurementOrderId ?? undefined,
    productionOrderId: dto.productionOrderId ?? undefined,
    deliveryConfirmation: dto.deliveryConfirmation
      ? {
          id: dto.deliveryConfirmation.id,
          orderId: dto.deliveryConfirmation.orderId,
          nfNumber: dto.deliveryConfirmation.nfNumber,
          driverName: dto.deliveryConfirmation.driverName,
          driverEmail: dto.deliveryConfirmation.driverEmail ?? undefined,
          deliveredAt: dto.deliveryConfirmation.deliveredAt,
          truckTemperature: dto.deliveryConfirmation.truckTemperature,
          receiverName: dto.deliveryConfirmation.receiverName,
          receiverDocument: dto.deliveryConfirmation.receiverDocument,
          signatureUrl: dto.deliveryConfirmation.signatureUrl,
          receiptPhotoUrl: dto.deliveryConfirmation.receiptPhotoUrl,
        }
      : null,
    assignedDriverId: dto.assignedDriverId ?? undefined,
    assignedDriverName: dto.assignedDriverName ?? undefined,
    fulfillmentMode: dto.fulfillmentMode ?? undefined,
  };
}

export function toOrderSummaryFromDetail(order: OrderDetail): OrderSummary {
  return {
    id: order.id,
    number: order.number,
    clientId: order.clientId,
    clientName: order.clientName,
    clientType: order.clientType,
    createdAt: order.createdAt,
    deliveryWindow: order.deliveryWindow,
    deliveryDate: order.deliveryDate,
    status: order.status,
    purchaseStartedAt: order.purchaseStartedAt,
    productionScheduledAt: order.productionScheduledAt,
    scheduledProductionDate: order.scheduledProductionDate,
    productionAutoAdvancedPending: order.productionAutoAdvancedPending,
    expeditionScheduledAt: order.expeditionScheduledAt,
    expeditionReleasedAt: order.expeditionReleasedAt,
    routeStartedAt: order.routeStartedAt,
    deliveredAt: order.deliveredAt,
    cancelledAt: order.cancelledAt,
    purchased: order.purchased,
    itemCount: order.items.length,
    // Todos os nomes distintos — a busca das listas admin filtra por este campo (truncado só no display).
    itemPreview: Array.from(new Set(order.items.map((item) => item.productName))),
    total: order.total,
    lastUpdatedAt: order.history[order.history.length - 1]?.at ?? order.createdAt,
    priceTableId: order.priceTableId,
    fulfillmentMode: order.fulfillmentMode,
  };
}

export async function fetchMyOrderSummaries(): Promise<OrderSummary[]> {
  const orders = await requestJson<OrderSummaryDto[]>("/api/v1/orders");
  return orders.map(toOrderSummary);
}

export async function fetchAdminOrderSummaries(): Promise<OrderSummary[]> {
  const orders = await requestJson<OrderSummaryDto[]>("/api/v1/admin/orders");
  return orders.map(toOrderSummary);
}

export async function fetchMyOrder(orderId: string): Promise<OrderDetail> {
  const order = await requestJson<OrderDetailDto>(`/api/v1/orders/${orderId}`);
  return toOrderDetail(order);
}

export async function fetchAdminOrder(orderId: string): Promise<OrderDetail> {
  const order = await requestJson<OrderDetailDto>(`/api/v1/admin/orders/${orderId}`);
  return toOrderDetail(order);
}

export async function fetchAdminOrderHistory(orderId: string) {
  const events = await requestJson<OrderHistoryEventDto[]>(`/api/v1/admin/orders/${orderId}/history`);
  return events.map((event) => ({
    type: event.type,
    status: event.status ?? undefined,
    title: event.title,
    details: event.details ?? undefined,
    actorEmail: event.actorEmail ?? undefined,
    actorRole: event.actorRole ?? undefined,
    at: event.at,
  }));
}

export async function checkoutOrder(input: OrderCheckoutPayload): Promise<OrderDetail> {
  const order = await requestJson<OrderDetailDto>("/api/v1/orders", {
    method: "POST",
    body: input,
  });
  return toOrderDetail(order);
}

export async function createManualOrder(input: ManualOrderPayload): Promise<OrderDetail> {
  const order = await requestJson<OrderDetailDto>("/api/v1/admin/orders", {
    method: "POST",
    body: input,
  });
  return toOrderDetail(order);
}

export async function updateMyOrder(orderId: string, input: OrderUpdatePayload): Promise<OrderDetail> {
  const order = await requestJson<OrderDetailDto>(`/api/v1/orders/${orderId}`, {
    method: "PATCH",
    body: input,
  });
  return toOrderDetail(order);
}

export async function deleteMyOrder(orderId: string): Promise<void> {
  await requestJson<void>(`/api/v1/orders/${orderId}`, {
    method: "DELETE",
  });
}

export interface ExpeditionPricingPreview {
  allPriced: boolean;
  total: string | null;
  totalRaw: number | null;
  conferenceSignature: string | null;
  lines: {
    product: string;
    cut: string | null;
    unit: string;
    quantity: string;
    unitPrice: string | null;
    subtotal: string | null;
    alreadyFrozen: boolean;
  }[];
  unpriced: string[];
}

/** Prévia dos preços resolvidos + total para a conferência de expedição (antes de confirmar). */
export async function fetchExpeditionPricingPreview(orderId: string): Promise<ExpeditionPricingPreview> {
  return requestJson<ExpeditionPricingPreview>(`/api/v1/admin/orders/${orderId}/expedition/pricing-preview`);
}

export async function transitionOrder(
  orderId: string,
  input: {
    toStatus: OrderStatus;
    transitionDate?: string;
    driverId?: string;
    fulfillmentMode?: FulfillmentMode;
    // Conferência de expedição (obrigatória em EM_EXPEDICAO → EM_ROTA).
    nfNumber?: string;
    boletoConfirmed?: boolean;
    boletoNote?: string;
    expectedTotal?: number;
    expectedSignature?: string;
  },
): Promise<OrderDetail> {
  const order = await requestJson<OrderDetailDto>(`/api/v1/admin/orders/${orderId}/transition`, {
    method: "POST",
    body: input,
  });
  return toOrderDetail(order);
}

/** Ajuste manual das datas de etapa (ADMIN/PLANEJADOR). Datas em ISO (yyyy-MM-dd); só envie as que mudar. */
export async function updateOrderDates(
  orderId: string,
  input: {
    deliveryDate?: string;
    deliveryWindow?: string;
    purchaseStartedAt?: string;
    productionScheduledAt?: string;
    expeditionScheduledAt?: string;
    expeditionReleasedAt?: string;
  },
): Promise<OrderDetail> {
  const order = await requestJson<OrderDetailDto>(`/api/v1/admin/orders/${orderId}/dates`, {
    method: "PATCH",
    body: input,
  });
  return toOrderDetail(order);
}

/**
 * Agenda (ou desagenda, quando `date` é null) o dia de produção de um pedido em CRIADO/EM_COMPRA.
 * Não avança o status — o scheduler do BE avança quando a data chega. `date` em ISO (yyyy-MM-dd).
 */
export async function scheduleOrderProductionDay(orderId: string, date: string | null): Promise<OrderDetail> {
  const order = await requestJson<OrderDetailDto>(`/api/v1/admin/orders/${orderId}/production-schedule`, {
    method: "PATCH",
    body: { scheduledProductionDate: date },
  });
  return toOrderDetail(order);
}

/** Restaura um pedido cancelado para o status escolhido (ADMIN/PLANEJADOR). */
export async function restoreOrder(orderId: string, toStatus: OrderStatus): Promise<OrderDetail> {
  const order = await requestJson<OrderDetailDto>(`/api/v1/admin/orders/${orderId}/restore`, {
    method: "POST",
    body: { toStatus },
  });
  return toOrderDetail(order);
}

export async function updateOrderStatus(orderId: string, status: OrderStatus): Promise<OrderDetail> {
  const order = await requestJson<OrderDetailDto>(`/api/v1/admin/orders/${orderId}/status`, {
    method: "PATCH",
    body: { status } satisfies OrderStatusPayload,
  });
  return toOrderDetail(order);
}

export async function startOrderPurchase(orderId: string): Promise<OrderDetail> {
  const order = await requestJson<OrderDetailDto>(`/api/v1/admin/orders/${orderId}/purchase/start`, {
    method: "PATCH",
  });
  return toOrderDetail(order);
}

export async function downloadPurchaseListExport(
  orderId: string,
  format: "pdf" | "xlsx",
): Promise<{ filename: string; blob: Blob }> {
  return downloadOrderResource(orderId, `purchase-list.${format}`, `lista-compra.${format}`);
}

/**
 * Exporta o pedido completo (dados do cliente, datas, entrega e itens). Endpoint distinto da lista de
 * compra; o backend é o gate de preço (PRODUCAO/SEPARACAO não recebem valores no arquivo).
 */
export async function downloadOrderExport(
  orderId: string,
  format: "pdf" | "xlsx",
): Promise<{ filename: string; blob: Blob }> {
  return downloadOrderResource(orderId, `order.${format}`, `pedido.${format}`);
}

/**
 * Documento da expedição (conference.pdf) com NF + financeiro, a partir do snapshot congelado. Lança
 * {@code NO_CONFERENCE} quando o pedido ainda não tem conferência (ex.: pedido antigo), para a UI avisar.
 */
export async function downloadConferencePdf(orderId: string): Promise<{ filename: string; blob: Blob }> {
  const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/orders/${orderId}/conference.pdf`, {
    credentials: "include",
  });
  if (response.status === 404) {
    throw new Error("NO_CONFERENCE");
  }
  if (response.status === 403) {
    throw new Error("FORBIDDEN_PDF");
  }
  if (!response.ok) {
    throw new Error("Nao foi possivel gerar o PDF da expedição");
  }
  const disposition = response.headers.get("content-disposition") ?? "";
  const match = /filename="?([^";]+)"?/i.exec(disposition);
  const filename = match?.[1] ?? `pedido_${orderId}.pdf`;
  return { filename, blob: await response.blob() };
}

async function downloadOrderResource(
  orderId: string,
  resource: string,
  fallbackName: string,
): Promise<{ filename: string; blob: Blob }> {
  const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/orders/${orderId}/${resource}`, {
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error("Nao foi possivel gerar o arquivo");
  }
  const disposition = response.headers.get("content-disposition") ?? "";
  const match = /filename="?([^";]+)"?/i.exec(disposition);
  const filename = match?.[1] ?? fallbackName;
  const blob = await response.blob();
  return { filename, blob };
}

export async function updateProductionListItem(
  orderId: string,
  input: {
    productId: string;
    unit: string;
    cut?: string | null;
    status: ProductionItemStatus;
    useStock?: boolean;
    producedQuantity?: number;
    reason?: string;
  },
): Promise<OrderDetail> {
  const order = await requestJson<OrderDetailDto>(`/api/v1/admin/orders/${orderId}/production-list/items`, {
    method: "PATCH",
    body: input,
  });
  return toOrderDetail(order);
}

export async function updateExpeditionListItem(
  orderId: string,
  input: {
    productId: string;
    unit: string;
    cut?: string | null;
    status: "PENDENTE" | "CARREGADO" | "EM_FALTA";
    loadedQuantity?: number;
    reason?: string;
  },
): Promise<OrderDetail> {
  const order = await requestJson<OrderDetailDto>(`/api/v1/admin/orders/${orderId}/expedition-list/items`, {
    method: "PATCH",
    body: input,
  });
  return toOrderDetail(order);
}

export async function updatePurchaseListItem(
  orderId: string,
  input: {
    productId: string;
    unit: string;
    purchased: boolean;
    useStock?: boolean;
    purchasedQuantity?: number;
    reason?: string;
  },
): Promise<OrderDetail> {
  const order = await requestJson<OrderDetailDto>(`/api/v1/admin/orders/${orderId}/purchase-list/items`, {
    method: "PATCH",
    body: input,
  });
  return toOrderDetail(order);
}

export async function cancelOrderItem(orderId: string, itemId: string): Promise<OrderDetail> {
  const order = await requestJson<OrderDetailDto>(`/api/v1/admin/orders/${orderId}/items/${itemId}/cancel`, {
    method: "POST",
  });
  return toOrderDetail(order);
}

export interface PatchOrderItemInput {
  quantity?: number;
  catalogUnitId?: string;
  cut?: string | null;
  /** `null` limpa a observação; `undefined` mantém. */
  observation?: string | null;
}

export async function patchOrderItem(
  orderId: string,
  itemId: string,
  input: PatchOrderItemInput,
): Promise<OrderDetail> {
  const body: Record<string, unknown> = {};
  if (input.quantity !== undefined) body.quantity = input.quantity;
  if (input.catalogUnitId !== undefined) body.catalogUnitId = input.catalogUnitId;
  if (input.cut === null) {
    body.clearCut = true;
  } else if (input.cut !== undefined) {
    body.cut = input.cut;
  }
  if (input.observation === null) {
    body.clearObservation = true;
  } else if (input.observation !== undefined) {
    body.observation = input.observation;
  }
  const order = await requestJson<OrderDetailDto>(`/api/v1/admin/orders/${orderId}/items/${itemId}`, {
    method: "PATCH",
    body,
  });
  return toOrderDetail(order);
}

export interface AddOrderItemInput {
  catalogProductId: string;
  catalogUnitId?: string;
  quantity: number;
  cut?: string | null;
  observation?: string | null;
}

export async function addOrderItem(
  orderId: string,
  input: AddOrderItemInput,
): Promise<OrderDetail> {
  const order = await requestJson<OrderDetailDto>(`/api/v1/admin/orders/${orderId}/items`, {
    method: "POST",
    body: input,
  });
  return toOrderDetail(order);
}
